import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { Subscription } from 'rxjs';
import { VoiceAllowedChangeInfo, VoiceSubscriptionType } from '../../../models/offerings-response.model';
import {
  ControlContainer,
  FormGroupDirective,
  FormGroup,
  Validators,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BindingType } from '@shared/models/subscription-offering.model';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { OfferingVariants } from '@shared/enums/offering-variants.enum';
import { select, Store } from '@ngxs/store';
import { MobilePhoneProduct } from '@sales/models/mobilephone-product';
import { CustomerProductMobilePhoneSelectors } from '@sales/states/customer-product-mobilephone/customer-product-mobilephone.selectors';
import { MobilePaymentType } from '@sales/enums/mobile-payment-type';
import { Seller } from '@core/models/dealer.model';
import { SponsorType } from '@shared/models/sponsor.model';
import { OfferingHelperService } from '@app/sales/services/offering-helper.service';
import { AssignedSubscriptionModel } from '@app/shared/models/assigned-subscription-offering.model';
import { CustomerProductsAccessorySelectors } from '@app/sales/states/accessory-product/accessory-product.selectors';
import { HardwareProduct } from '@app/shared/models/hardware-product.model';
import { VoiceOfferingItemComponent } from './voice-offering-item/voice-offering-item.component';
import { VasVoiceOfferingItemComponent } from './vas-voice-offering-item/vas-voice-offering-item.component';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { IceSearchFieldComponent } from '@app/shared/components/molecules/ice-search-field/ice-search-field.component';
import { CustomerSelectors } from '@app/sales/modules/details/states/customer.selectors';
import { getCurrentAge } from '@app/sales/validators/age.validator';

enum OfferingFilters {
  WithoutBinding = 'Uten binding',
  WithTerminalBinding = 'Terminal',
  EquipmentUpgrade = 'MobilBytte',
  All = 'Alle',
  Ordinary = 'Ordinære',
  AgeLimitedUpper = 'Barn og unge',
  AgeLimitedLower = 'Senior',
  Discounted = 'Årsavtale og rabatt',
}

const offeringFilterMap = new Map<OfferingFilters, ((offering: VoiceAllowedChangeInfo) => unknown) | null>([
  [
    OfferingFilters.WithoutBinding,
    offering => !offering.subscriptionOffering.bindings || offering.subscriptionOffering.bindings.length === 0,
  ],
  [
    OfferingFilters.WithTerminalBinding,
    offering =>
      offering.subscriptionOffering.bindings?.length > 0 &&
      offering.subscriptionOffering.bindings.find(b => b.bindingType === BindingType.Handset),
  ],
  [OfferingFilters.EquipmentUpgrade, offering => offering.subscriptionOffering.isEquipmentUpgradeCompatible],
  [OfferingFilters.All, null],
  [
    OfferingFilters.AgeLimitedUpper,
    offering => offering.subscriptionOffering.upperAgeLimit && !offering.subscriptionOffering.monthlyPrice.isCampaign,
  ],
  [
    OfferingFilters.AgeLimitedLower,
    offering => offering.subscriptionOffering.lowerAgeLimit && !offering.subscriptionOffering.monthlyPrice.isCampaign,
  ],
  [
    OfferingFilters.Ordinary,
    offering =>
      offering.subscriptionOffering.variant === OfferingVariants.None &&
      !offering.subscriptionOffering.upperAgeLimit &&
      !offering.subscriptionOffering.lowerAgeLimit &&
      !offering.subscriptionOffering.monthlyPrice.isCampaign,
  ],
  [
    OfferingFilters.Discounted,
    offering =>
      offering.subscriptionOffering.variant === OfferingVariants.SimBindingAgreement ||
      offering.subscriptionOffering.monthlyPrice.isCampaign,
  ],
]);

@Component({
  selector: 'app-order-builder-step',
  templateUrl: './order-builder-step.component.html',
  styleUrls: ['./order-builder-step.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IceSearchFieldComponent,
    MatButtonToggleGroup,
    MatButtonToggle,
    VasVoiceOfferingItemComponent,
    VoiceOfferingItemComponent,
  ],
})
export class OrderBuilderStepComponent implements OnInit, OnDestroy, OnChanges {
  private formGroupDirective = inject(FormGroupDirective);
  private store = inject(Store);

  private customerBirthdate = select(CustomerSelectors.customerBirthdate);

  PriceType = PriceType;
  getVasProductFromOffering = OfferingHelperService.getVasProductFromOffering;

  @Input() product: VoiceProduct;
  @Input() assignedSubscription: AssignedSubscriptionModel = null;
  @Input() allowedOfferings: VoiceAllowedChangeInfo[];
  @Input() showOfferings: boolean = true;
  @Input() canSellMobilePhone: boolean;
  @Input() isEquipmentUpgradeEnabled: boolean;
  @Output() productChanged = new EventEmitter();
  @Output() isInvalid = new EventEmitter();
  @Output() sponsorSelected = new EventEmitter<{ productId: string; seller: Seller }>();
  @Output() sponsorTypeSelected = new EventEmitter<{ productId: string; sponsorType: SponsorType }>();

  form: FormGroup;
  initializedOfferings: VoiceAllowedChangeInfo[];
  allowedOfferingsFiltered: VoiceAllowedChangeInfo[];
  allowedOfferingsTypesOnTop: VoiceSubscriptionType[];
  hasYearAgreementOfferings: boolean;
  searchValue = '';
  offeringFilters = OfferingFilters;
  offeringFiltersValue = OfferingFilters.All;
  activeFilters: OfferingFilters[] = [];

  subscriptions$: Subscription[] = [];

  static buildChooseSubscriptionFormGroup(offering: string) {
    return new FormGroup({ offering: new FormControl(offering, [Validators.required]) });
  }

  static getChooseSubscriptionFormStateValue(product: VoiceProduct) {
    return { offering: product.offering?.id };
  }

  static updateOfferingOnFormState(productInFormState, offeringId: string) {
    return { ...productInFormState, chooseSubscription: { offering: offeringId } };
  }

  ngOnInit() {
    this.setFilteredOfferings();

    this.hasYearAgreementOfferings = this.initializedOfferings?.some(
      allowedOffering => allowedOffering.subscriptionOffering.variant === OfferingVariants.SimBindingAgreement
    );

    this.formGroupDirective.form.addControl(
      'chooseSubscription',
      OrderBuilderStepComponent.buildChooseSubscriptionFormGroup(
        this.product?.offering?.id ? this.product?.offering?.id : this.assignedSubscription?.subscriptionNo?.toString()
      )
    );

    this.form = this.formGroupDirective.form.get('chooseSubscription') as FormGroup;
    this.subscriptions$.push(
      this.form.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe(val => {
        this.isInvalid.emit(this.form.invalid);
        if (this.assignedSubscription && val.offering === this.assignedSubscription.subscriptionNo) {
          this.productChanged.emit({
            productId: this.product.id,
            offering: this.assignedSubscription,
            changeInfo: null,
          });
        } else {
          const selectedOffering = this.initializedOfferings?.find(
            offering => offering.subscriptionOffering.id === val.offering
          );
          if (val.offering && selectedOffering) {
            this.productChanged.emit({
              productId: this.product.id,
              offering: selectedOffering.subscriptionOffering,
              changeInfo: selectedOffering.changeInfo,
            });
          }
        }
      })
    );
    this.isInvalid.emit(this.form.invalid);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.allowedOfferings) {
      this.setFilteredOfferings();
    }
  }

  private setFilteredOfferings() {
    this.initializedOfferings = this.allowedOfferings;
    this.activeFilters = [];

    if (this.product.accessoryProductId) {
      this.offeringFiltersValue = OfferingFilters.WithTerminalBinding;
      this.activeFilters.push(OfferingFilters.WithTerminalBinding);
      const allTerminalOfferings = this.getAllTerminalOfferings();
      const accessoryProduct = this.store
        .selectSnapshot<HardwareProduct[]>(CustomerProductsAccessorySelectors.newAccessoryProducts)
        .find(x => x.id === this.product.accessoryProductId);
      const allowedTerminalOfferingIds = accessoryProduct?.accessoryVariant?.subscriptionBundleOptions?.map(
        o => o.subscriptionOfferingId
      );

      this.initializedOfferings = allTerminalOfferings.filter(o =>
        allowedTerminalOfferingIds?.includes(o.subscriptionOffering.id)
      );
      this.allowedOfferingsFiltered = this.filterOnSearchText(this.searchValue, this.initializedOfferings);
    } else if (this.product.mobileProductId) {
      const mobileProduct = this.store
        .selectSnapshot<MobilePhoneProduct[]>(CustomerProductMobilePhoneSelectors.mobilePhoneProducts)
        .find(x => x.id === this.product.mobileProductId);
      if (mobileProduct?.paymentType === MobilePaymentType.EquipmentUpgrade) {
        this.activeFilters.push(OfferingFilters.EquipmentUpgrade);
        this.offeringFiltersValue = OfferingFilters.EquipmentUpgrade;
        this.onFilterChange(this.searchValue, { value: OfferingFilters.EquipmentUpgrade });
      } else if (mobileProduct?.paymentType === MobilePaymentType.PayNowWithSubscription) {
        this.offeringFiltersValue = OfferingFilters.WithTerminalBinding;
        this.activeFilters.push(OfferingFilters.WithTerminalBinding);
        const allTerminalOfferings = this.getAllTerminalOfferings();
        const allowedTerminalOfferingIds = mobileProduct.isManualRegistration
          ? allTerminalOfferings.map(o => o.subscriptionOffering.id)
          : mobileProduct.mobilePhoneVariant?.paymentInformation?.subscriptionBundleOptions.map(
              o => o.subscriptionOfferingId
            );
        this.initializedOfferings = allTerminalOfferings.filter(o =>
          allowedTerminalOfferingIds.includes(o.subscriptionOffering.id)
        );
        this.allowedOfferingsFiltered = this.filterOnSearchText(this.searchValue, this.initializedOfferings);
      } else {
        // remove terminal bindings
        this.initializedOfferings = this.initializedOfferings?.filter(
          o =>
            !(
              o.subscriptionOffering.bindings?.length > 0 &&
              o.subscriptionOffering.bindings.find(b => b.bindingType === BindingType.Handset)
            )
        );
        this.resetFilters();
      }
    } else {
      // remove terminal bindings
      this.initializedOfferings = this.initializedOfferings?.filter(
        o =>
          !(
            o.subscriptionOffering.bindings?.length > 0 &&
            o.subscriptionOffering.bindings.find(b => b.bindingType === BindingType.Handset)
          )
      );
      this.resetFilters();
    }
  }

  private getAllTerminalOfferings() {
    return this.allowedOfferings.filter(
      offering =>
        offering.subscriptionOffering.bindings?.length > 0 &&
        offering.subscriptionOffering.bindings.find(b => b.bindingType === BindingType.Handset)
    );
  }

  private resetFilters() {
    if (
      this.offeringFiltersValue === OfferingFilters.WithTerminalBinding ||
      this.offeringFiltersValue === OfferingFilters.EquipmentUpgrade ||
      !this.allowedOfferingsFiltered ||
      this.allowedOfferingsFiltered?.length === 0
    ) {
      this.setDefaultFilters(this.getDefaultFilter());
    } else {
      this.setDefaultFilters(this.offeringFiltersValue);
    }
  }

  private getDefaultFilter(): OfferingFilters {
    const birthdate = this.customerBirthdate();
    if (!birthdate) {
      return OfferingFilters.Ordinary;
    }

    const customerAge = getCurrentAge(birthdate);
    const isEligibleForLowerAgeLimitedOffering = this.getFilteredAllowedOfferings(OfferingFilters.AgeLimitedLower)?.some(
      offering => customerAge >= offering.subscriptionOffering.lowerAgeLimit
    );
    const isEligibleForUpperAgeLimitedOffering = this.getFilteredAllowedOfferings(OfferingFilters.AgeLimitedUpper)?.some(
      offering => customerAge <= offering.subscriptionOffering.upperAgeLimit
    );

    return isEligibleForLowerAgeLimitedOffering
      ? OfferingFilters.AgeLimitedLower
      : isEligibleForUpperAgeLimitedOffering
        ? OfferingFilters.AgeLimitedUpper
        : OfferingFilters.Ordinary;
  }

  private setDefaultFilters(defaultFilter: OfferingFilters) {
    if (this.getFilteredAllowedOfferings(OfferingFilters.Ordinary)?.length > 0) {
      this.activeFilters.push(OfferingFilters.Ordinary);
    }
    if (this.getFilteredAllowedOfferings(OfferingFilters.AgeLimitedUpper)?.length > 0) {
      this.activeFilters.push(OfferingFilters.AgeLimitedUpper);
    }
    if (this.getFilteredAllowedOfferings(OfferingFilters.AgeLimitedLower)?.length > 0) {
      this.activeFilters.push(OfferingFilters.AgeLimitedLower);
    }
    if (this.getFilteredAllowedOfferings(OfferingFilters.Discounted)?.length > 0) {
      this.activeFilters.push(OfferingFilters.Discounted);
    }
    if (this.getFilteredAllowedOfferings(OfferingFilters.All)?.length > 0) {
      this.activeFilters.push(OfferingFilters.All);
    }

    if (!this.activeFilters.includes(defaultFilter)) {
      this.activeFilters.push(defaultFilter);
    }

    this.offeringFiltersValue = defaultFilter;
    this.onFilterChange(this.searchValue, { value: defaultFilter });
  }

  onFilterChange(searchValue: string, { value }: { value: OfferingFilters }) {
    this.searchValue = searchValue;
    this.offeringFiltersValue = value;
    this.allowedOfferingsTypesOnTop = null;

    this.allowedOfferingsFiltered = this.getFilteredAllowedOfferings(value);

    if (value === this.offeringFilters.AgeLimitedUpper) {
      this.allowedOfferingsTypesOnTop = [VoiceSubscriptionType.IceJuniorPremium, VoiceSubscriptionType.IceJunior];
    } else if (value === this.offeringFilters.AgeLimitedLower) {
      this.allowedOfferingsTypesOnTop = [VoiceSubscriptionType.IceSenior];
    } else if (value === this.offeringFilters.Ordinary) {
      this.allowedOfferingsTypesOnTop = [VoiceSubscriptionType.Trial];
    }

    this.allowedOfferingsFiltered = this.filterOnSearchText(searchValue, this.allowedOfferingsFiltered);
  }

  onSponsorSelected(seller: Seller) {
    this.sponsorSelected.emit({ productId: this.product.id, seller: seller });
  }

  onSponsorTypeSelected(sponsorType: SponsorType) {
    this.sponsorTypeSelected.emit({ productId: this.product.id, sponsorType: sponsorType });
  }

  private filterOnSearchText(searchValue: string, offerings: VoiceAllowedChangeInfo[]) {
    if (searchValue.length > 0) {
      return offerings?.filter((offering: VoiceAllowedChangeInfo) =>
        offering.subscriptionOffering.name.toLocaleLowerCase().includes(searchValue.toLowerCase())
      );
    }
    return offerings;
  }

  private getFilteredAllowedOfferings(filter: OfferingFilters) {
    const filterFn = offeringFilterMap.get(filter);
    return filterFn ? this.initializedOfferings?.filter(filterFn) : this.initializedOfferings;
  }

  sortOfferingsByMobilePlanTypeAndPrice(
    offerings: VoiceAllowedChangeInfo[],
    types: VoiceSubscriptionType[] = []
  ): VoiceAllowedChangeInfo[] {
    const sortedSubscriptionOfferings = OfferingHelperService.sortOfferingsByMobilePlanTypeAndDataVolumeAndPriceDesc(
      offerings?.map(offering => offering.subscriptionOffering),
      types
    );

    return sortedSubscriptionOfferings.map(subscriptionOffering =>
      offerings?.find(offering => offering.subscriptionOffering.id === subscriptionOffering.id)
    );
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
